import { getFormattedDate } from '@app/utils/date'

import { dateFormat } from '@app/constants'

import { CreateMonitoringScheduleDto, EditMonitoringScheduleDto } from '@app/api/routes/monitoringSchedules/dtos'

import { NewMonitoringScheduleData } from '@app/pages/MonitoringSchedulePage/types'

import type { TableData } from '@app/types/table'
import type { MonitoringScheduleDataSource } from '@app/pages/MonitoringSchedulePage/types'
import type { ResponseDto } from '@app/api/dtos'
import type { MonitoringSchedulesListsDto } from '@app/api/routes/monitoringSchedules/dtos'

export const adaptMonitoringScheduleTableData = (
  monitoringSchedulesListsResponse: ResponseDto<MonitoringSchedulesListsDto>
): TableData<MonitoringScheduleDataSource> => {
  const { count, results: monitoringSchedulesLists } = monitoringSchedulesListsResponse

  const dataSource: MonitoringScheduleDataSource[] = monitoringSchedulesLists.map(dto => ({
    key: dto.id,
    monitoringType: dto.monitoringType?.name ?? null,
    monitoringSubtype: dto.monitoringSubtype?.name ?? null,
    store: dto.store?.name ?? null,
    cluster: dto.cluster?.name ?? null,
    clustersGroup: dto.clustersGroup?.name ?? null,
    salesOrganization: dto.salesOrganization?.name ?? null,
    monitoringStartDayOfWeek: dto.monitoringStartDayOfWeek,
    monitoringStartAtTime: getFormattedDate(dto.monitoringStartAtTime, dateFormat.HH_mm, {
      extractionFormat: dateFormat.HH_mm_ss
    }),
    receivingDataDeadlineDays: dto.receivingDataDeadlineDays,
    receivingDataDeadlineTime: getFormattedDate(dto.receivingDataDeadlineTime, dateFormat.HH_mm, {
      extractionFormat: dateFormat.HH_mm_ss
    }),
    processingDataDeadlineDays: dto.processingDataDeadlineDays,
    processingDataDeadlineTime: getFormattedDate(dto.processingDataDeadlineTime, dateFormat.HH_mm, {
      extractionFormat: dateFormat.HH_mm_ss
    }),
    newDataDeadlineDays: dto.newDataDeadlineDays,
    newDataDeadlineTime: getFormattedDate(dto.newDataDeadlineTime, dateFormat.HH_mm, {
      extractionFormat: dateFormat.HH_mm_ss
    })
  }))

  return { count, dataSource }
}

export const adaptCreateMonitoringSchedule = (
  monitoringScheduleData: NewMonitoringScheduleData
): CreateMonitoringScheduleDto => {
  return {
    monitoringTypeIds:
      monitoringScheduleData.monitoringTypeIds.length > 0 ? monitoringScheduleData.monitoringTypeIds : [],
    monitoringSubtypeIds:
      monitoringScheduleData.monitoringSubtypeIds.length > 0 ? monitoringScheduleData.monitoringSubtypeIds : [],
    storeIds: monitoringScheduleData.storeIds.length > 0 ? monitoringScheduleData.storeIds : [],
    clusterIds: monitoringScheduleData.clusterIds.length > 0 ? monitoringScheduleData.clusterIds : [],
    clustersGroupIds: monitoringScheduleData.clustersGroupIds.length > 0 ? monitoringScheduleData.clustersGroupIds : [],
    monitoringStartDayOfWeek: parseInt(monitoringScheduleData.dateOfStartMonitoring),
    monitoringStartAtTime: getFormattedDate(monitoringScheduleData.timeOfStartMonitoring, dateFormat.HH_mm),
    receivingDataDeadlineDays: parseInt(monitoringScheduleData.dateOfReceiveData),
    receivingDataDeadlineTime: getFormattedDate(monitoringScheduleData.timeOfReceiveData, dateFormat.HH_mm),
    processingDataDeadlineDays: parseInt(monitoringScheduleData.dateOfProcessingData),
    processingDataDeadlineTime: getFormattedDate(monitoringScheduleData.timeOfProcessingData, dateFormat.HH_mm),
    newDataDeadlineDays: parseInt(monitoringScheduleData.dateOfSettingNewPriceData),
    newDataDeadlineTime: getFormattedDate(monitoringScheduleData.timeOfSettingNewPriceData, dateFormat.HH_mm)
  }
}

export const adaptEditMonitoringSchedule = (
  monitoringScheduleData: NewMonitoringScheduleData
): EditMonitoringScheduleDto => {
  return {
    monitoringTypeIds: monitoringScheduleData.monitoringTypeIds?.[0] ?? '',
    monitoringSubtypeIds: monitoringScheduleData.monitoringSubtypeIds?.[0] ?? '',
    storeIds: monitoringScheduleData.storeIds?.[0] ?? '',
    clusterIds: monitoringScheduleData.clusterIds?.[0] ?? '',
    clustersGroupIds: monitoringScheduleData.clustersGroupIds?.[0] ?? '',
    monitoringStartDayOfWeek: parseInt(monitoringScheduleData.dateOfStartMonitoring),
    monitoringStartAtTime: getFormattedDate(monitoringScheduleData.timeOfStartMonitoring, dateFormat.HH_mm),
    receivingDataDeadlineDays: parseInt(monitoringScheduleData.dateOfReceiveData),
    receivingDataDeadlineTime: getFormattedDate(monitoringScheduleData.timeOfReceiveData, dateFormat.HH_mm),
    processingDataDeadlineDays: parseInt(monitoringScheduleData.dateOfProcessingData),
    processingDataDeadlineTime: getFormattedDate(monitoringScheduleData.timeOfProcessingData, dateFormat.HH_mm),
    newDataDeadlineDays: parseInt(monitoringScheduleData.dateOfSettingNewPriceData),
    newDataDeadlineTime: getFormattedDate(monitoringScheduleData.timeOfSettingNewPriceData, dateFormat.HH_mm)
  }
}
